import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can, normRole, ROLE_LABELS } from "@/lib/permissions";
import { NotificationsBell } from "@/components/NotificationsBell";
import { QRScannerButton } from "@/components/QRScanner";
import { Copilot } from "@/components/Copilot";
import {
  LayoutDashboard, Package, Users, TrendingUp, Settings, LogOut,
  Bell, Search, ChevronDown, Menu, Hammer, Gem, Layers, Coins,
  Factory, ClipboardCheck, PenTool, Receipt, ShieldCheck, MessageCircle,
  Camera, Route as RouteIcon, Wallet, Warehouse, ShoppingCart, UserCheck,
  Database, Zap, Phone, ListTodo, BarChart3, FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { BrandLogo } from "@/components/BrandLogo";
import GlobalSearch from "@/components/GlobalSearch";

// Group nav items — grouped visually in sidebar for clarity
function buildNav(user, modules) {
  if (!user) return [];
  const role = normRole(user.role);
  const m = modules || {};
  // Backwards-compat: if the company predates the field, treat all as enabled.
  const on = (key) => m[key] === undefined ? true : Boolean(m[key]);

  if (role === "production") {
    return [{ heading: null, items: [{ to: "/production/my-queue", label: "My Queue", icon: Hammer }] }];
  }
  if (role === "client") {
    return [{ heading: null, items: [{ to: "/orders", label: "My Orders", icon: Package }] }];
  }

  const groups = [];

  // Overview
  const overview = [];
  if (can(user, "platform.super_admin")) overview.push({ to: "/super-admin", label: "Platform", icon: ShieldCheck });
  if (can(user, "platform.super_admin")) overview.push({ to: "/super-admin/contacts", label: "Master Contacts", icon: Database });
  if (can(user, "platform.super_admin")) overview.push({ to: "/super-admin/promotion-rules", label: "Auto-Promote", icon: Zap });
  if (can(user, "platform.super_admin")) overview.push({ to: "/super-admin/voice-campaigns", label: "Voice Campaigns", icon: Phone });
  if (can(user, "dashboard.view")) overview.push({ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard });
  if (overview.length) groups.push({ heading: "Overview", items: overview });

  // Sales & CRM
  const sales = [];
  if (can(user, "orders.view")) sales.push({ to: "/orders", label: "Orders", icon: Package });
  if (can(user, "contacts.view")) sales.push({ to: "/contacts", label: "Contacts", icon: Users });
  if (can(user, "deals.view")) sales.push({ to: "/deals", label: "Pipeline", icon: TrendingUp });
  if (sales.length) groups.push({ heading: "Sales & CRM", items: sales });

  // Field
  const field = [];
  if (on("field_sales")) {
    if (can(user, "field.own")) field.push({ to: "/field/visits", label: "Field Visits", icon: RouteIcon });
    if (can(user, "field.own")) field.push({ to: "/field/routes", label: "Route Planner", icon: RouteIcon });
    if (can(user, "field.own")) field.push({ to: "/field/scan", label: "Scan Card", icon: Camera });
    if (can(user, "field.own")) field.push({ to: "/field/follow-ups", label: "Follow-ups", icon: ListTodo });
    if (can(user, "field.own") || can(user, "field.expenses.reimburse")) field.push({ to: "/field/expenses", label: "Travel Expenses", icon: Wallet });
    if (can(user, "field.team.view")) field.push({ to: "/field/team", label: "Team Journey", icon: RouteIcon });
    if (can(user, "field.team.view")) field.push({ to: "/field/performance", label: "Team Performance", icon: BarChart3 });
    if (["admin","super-admin","manager","sales","order_team"].includes(user?.role))
      field.push({ to: "/field/extras", label: "Territories · Targets · Consignments", icon: BarChart3 });
  }
  if (field.length) groups.push({ heading: "Field Sales", items: field });

  // Production
  const production = [];
  if (can(user, "production.board")) production.push({ to: "/production/board", label: "Production Board", icon: Factory });
  if (can(user, "cad.quotes.view")) production.push({ to: "/cad/quotes", label: "CAD Quotes", icon: PenTool });
  if (can(user, "cad.files.view")) production.push({ to: "/cad/files", label: "CAD Ledger", icon: ClipboardCheck });
  if (production.length) groups.push({ heading: "Production", items: production });

  // Catalog
  const catalog = [];
  if (can(user, "catalog.view")) catalog.push({ to: "/products", label: "Products", icon: Gem });
  if (can(user, "collections.view")) catalog.push({ to: "/collections", label: "Collections", icon: Layers });
  if (can(user, "catalog.shares.view")) catalog.push({ to: "/catalog-shares", label: "WhatsApp Shares", icon: MessageCircle });
  if (can(user, "gold_rates.view")) catalog.push({ to: "/gold-rates", label: "Metal Rates", icon: Coins });
  if (catalog.length) groups.push({ heading: "Catalog", items: catalog });

  // Operations
  const ops = [];
  if (on("assets") && can(user, "assets.view")) ops.push({ to: "/assets", label: "Factory Assets", icon: ShieldCheck });
  if (on("work_costing") && ["admin","super-admin","manager","production","karigar","accounts"].includes(user?.role)) ops.push({ to: "/work-costing", label: "Work Costing", icon: Coins });
  if (on("design_search") && ["admin","super-admin","manager","sales","order_team"].includes(user?.role)) ops.push({ to: "/design-search", label: "Design Search", icon: Gem });
  if (on("attendance") && ["admin","super-admin","manager","production","karigar","order_team","cad_designer","accounts","sales","watchman"].includes(user?.role)) ops.push({ to: "/attendance", label: "Attendance", icon: UserCheck });
  if (on("procurement") && can(user, "procurement.inward")) ops.push({ to: "/procurement", label: user?.role === "watchman" ? "Goods Inward" : "Procurement", icon: Warehouse });
  if (on("visitors") && (can(user, "visitors.view") || user?.role === "watchman")) ops.push({ to: "/visitors", label: "Visitors", icon: UserCheck });
  if (on("hr_payroll") && ["admin","super-admin","manager","accounts"].includes(user?.role)) ops.push({ to: "/hr", label: "HR · Payroll", icon: Wallet });
  // Every employee can view their own payslips even without accounts role
  else if (on("hr_payroll") && user?.role && !["admin","super-admin","manager","accounts"].includes(user?.role)) ops.push({ to: "/hr", label: "My Payslips", icon: Wallet });
  if (ops.length) groups.push({ heading: "Operations", items: ops });

  // Finance
  const finance = [];
  if (can(user, "invoices.view")) finance.push({ to: "/invoices", label: "Invoices", icon: Receipt });
  if (finance.length) groups.push({ heading: "Finance", items: finance });

  // Admin
  const admin = [];
  if (["admin","super-admin","manager","accounts"].includes(user?.role)) admin.push({ to: "/import", label: "Import hub", icon: FileUp });
  if (["admin","super-admin","manager","accounts"].includes(user?.role)) admin.push({ to: "/cash-flow", label: "Cash-flow Forecast", icon: BarChart3 });
  admin.push({ to: "/audit-hub", label: "Audit Hub", icon: ShieldCheck });
  admin.push({ to: "/plan", label: "Your Plan", icon: ShieldCheck });
  if (can(user, "settings.roles")) admin.push({ to: "/settings/roles", label: "Roles & Access", icon: ShieldCheck });
  if (on("encharge") && ["admin","super-admin","manager"].includes(user?.role)) admin.push({ to: "/settings/encharge", label: "Email · Encharge", icon: MessageCircle });
  if (can(user, "settings.view")) admin.push({ to: "/settings", label: "Settings", icon: Settings });
  if (admin.length) groups.push({ heading: "Admin", items: admin });

  return groups;
}

// Flatten grouped nav to a plain list — used by profile dropdown + bottom nav.
function flatItems(groups) {
  return groups.flatMap((g) => g.items);
}

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}

export function AppShell({ children }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const groups = buildNav(user, company?.modules);
  const items = flatItems(groups);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const roleLabel = ROLE_LABELS[user?.role] || user?.role || "";

  const renderGroups = (onNavigate) => (
    <>
      {groups.map((g, gi) => (
        <div key={g.heading || `group-${gi}`} className={gi === 0 ? "" : "mt-4"}>
          {g.heading && (
            <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]/70 font-medium">
              {g.heading}
            </div>
          )}
          <div className="space-y-1">
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={onNavigate}
                className={({ isActive }) => `side-item ${isActive ? "active" : ""}`}
                data-testid={`nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
              >
                <it.icon size={18} strokeWidth={1.5} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAF7EF] bg-grain flex">
      {/* Sidebar - desktop (deep green) */}
      <aside className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-[#083F22] bg-[#083F22] sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <BrandLogo size={40} showWordmark tagline variant="dark" testId="sidebar-brand"/>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]/80 mt-3">
            {company?.name || "Jewellery Suite"}
          </div>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {renderGroups()}
        </nav>
        <div className="p-4 border-t border-white/10 text-[10px] uppercase tracking-[0.2em] text-[#E4D3A0]/60">
          <div>v1.0 · {company?.code}</div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-[#FAF7EF]/85 backdrop-blur-xl border-b border-[#E4DFCF]">
          <div className="flex items-center gap-3 px-4 lg:px-8 h-16">
            <button
              className="lg:hidden -ml-1 p-2 rounded-lg hover:bg-[#F1EDDF]"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              <Menu size={20} />
            </button>
            <div className="lg:hidden">
              <BrandLogo size={32} showWordmark testId="topbar-brand"/>
            </div>

            {can(user, "search.global") && <GlobalSearch />}

            <div className="ml-auto flex items-center gap-2">
              <QRScannerButton/>
              <NotificationsBell/>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-[#F1EDDF]" data-testid="profile-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-[#0F5E39] text-white text-xs font-medium">
                        {initials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium leading-tight">{user?.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#4A5A50]">{roleLabel}{user?.department ? ` · ${user.department}` : ""}</div>
                    </div>
                    <ChevronDown size={14} className="text-[#4A5A50]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuLabel>
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-xs text-[#4A5A50]">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {items.slice(0, 6).map(it => (
                    <DropdownMenuItem key={it.to} onClick={() => navigate(it.to)} data-testid={`menu-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}>{it.label}</DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); navigate("/login"); }}
                    className="text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut size={14} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8">{children}</main>

        {/* Bottom nav - mobile */}
        <nav className="lg:hidden bottom-nav">
          <div className="grid grid-cols-4">
            {items.slice(0, 4).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[10px] uppercase tracking-widest ${isActive ? "text-[#0F5E39]" : "text-[#4A5A50]"}`}
                data-testid={`bottom-nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
              >
                <it.icon size={20} strokeWidth={1.5} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      {/* Mobile drawer (dark green) */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMobileOpen(false)}>
          <aside
            className="absolute left-0 top-0 h-full w-[280px] bg-[#083F22] p-4 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-drawer"
          >
            <BrandLogo size={36} showWordmark tagline variant="dark"/>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]/80 mt-4 mb-3">{company?.name}</div>
            {renderGroups(() => setMobileOpen(false))}
          </aside>
        </div>
      )}

      {/* Global Copilot — floating trigger + Cmd/Ctrl-K palette */}
      <Copilot/>
    </div>
  );
}
